// Quote Builder — 9 "simple" category pickers.
//
// Verbatim port of kos-qb-v3.js:2973–3271 (renderers) and the matching
// add-to-quote shapes at kos-qb-v3.js:4468–4523 (`genericPickerAdd`).
//
// Each picker is a thin config-driven wrapper around a shared shell:
//   handles, storage, worktops, panels, plinth, lighting, appliances,
//   sundries, labour.
//
// Picker state mirrors source `blankPickerState(cat)` defaults
// (kos-qb-v3.js:2400–2462). Supplier dropdown shows all six entries from
// PICKER_SUPPLIERS (TKC/DTN/LDL/PWS/Omega/Manual). Labour is a special
// case: no supplier select; `supplier` field on the line is hard-coded
// to "Labour" per source line 4507.

import type { ItemCategory, QuoteItem } from "./types";
import { PICKER_SUPPLIERS } from "./types";
import type { QuoteStore } from "./state";
import { escapeHtml, ICONS, newId, svgIcon } from "./formatters";
import { recalcItem } from "./pricing";
import { bumpFav, fuzzyFilter, highlight } from "./fuzzy";
import { getCachedProducts, loadAllProducts, type ProductRecord } from "./products-cache";

// ── Per-category type lists (verbatim source) ─────────────────────────────

const WORKTOP_MATERIALS = [
  "Laminate", "Quartz", "Granite", "Solid Surface (Corian)", "Solid Wood",
  "Compact", "Sintered Stone (Dekton/Neolith)", "Porcelain", "Marble",
];
const WORKTOP_EDGES = [
  "Square", "Bevelled", "Bullnose", "Pencil round",
  "Mitred", "Postformed", "Fully postformed",
];
const PANEL_TYPES = [
  "Base End Panel", "Wall End Panel", "Tall End Panel",
  "Breakfast Bar Back Panel", "Filler Panel", "Decorative Panel",
  "Shelf", "Glass Shelf", "Splashback", "Upstand", "Other",
];
const PLINTH_TYPES = [
  "Plinth", "Cornice", "Pelmet", "Pilaster",
  "Quadrant", "Scotia Trim", "Light Trim",
];
const LIGHTING_TYPES = [
  "LED Strip", "Plinth Lighting", "Cabinet Spotlight", "Pelmet Lighting",
  "Pendant", "Driver / Transformer", "Sensor", "Connector", "Other Lighting",
];
const APPLIANCE_TYPES = [
  "Oven", "Combination Oven", "Microwave",
  "Hob · Induction", "Hob · Gas", "Hob · Ceramic",
  "Range Cooker", "Cooker Hood / Extractor",
  "Wine Cooler", "Fridge", "Freezer", "Fridge / Freezer",
  "Dishwasher", "Washing Machine", "Tumble Dryer",
  "Boiling Tap", "Other Appliance",
];
const SUNDRY_TYPES = [
  "Brackets", "Connectors", "Sealant", "Screws / Fixings",
  "Adhesive", "Edge Banding", "Cable Management",
  "Worktop Joint", "Sundry / Other",
];
const LABOUR_TYPES = [
  "Installation", "Survey", "Project Management", "Removal / Disposal",
  "Plumbing", "Electrical", "Plastering", "Tiling", "Decorating",
  "Worktop Templating + Fitting", "Delivery", "Other",
];

// ── Shared shell helpers ──────────────────────────────────────────────────

interface BaseState {
  zoneId: string;
  supplier: string;
  qty: number;
  listPrice: number;
  sku: string;
  desc: string;
}

function zoneOpts(store: QuoteStore, selId: string): string {
  return store.get().zones.map(
    (z) => `<option value="${escapeHtml(z.id)}"${z.id === selId ? " selected" : ""}>${escapeHtml(z.name)}</option>`,
  ).join("");
}

function supplierOpts(selKey: string): string {
  return PICKER_SUPPLIERS.map((sup) => {
    const modelLabel = sup.model === "completes" ? "completes" : "mix & match";
    return `<option value="${escapeHtml(sup.key)}"${sup.key === selKey ? " selected" : ""}${sup.disabled ? " disabled" : ""}>${escapeHtml(sup.key)} (${modelLabel})</option>`;
  }).join("");
}

function typeOpts(list: readonly string[], selVal: string): string {
  return list.map((t) => `<option${t === selVal ? " selected" : ""}>${escapeHtml(t)}</option>`).join("");
}

function defaultDisc(supplier: string): number {
  return PICKER_SUPPLIERS.find((s) => s.key === supplier)?.defaultDiscPct ?? 0;
}

function footer(label: string, hintOverride?: string): string {
  const hint = hintOverride ?? "<kbd>Enter</kbd> add  ·  <kbd>Esc</kbd> close";
  return `
    <div class="qb-door-foot">
      <div class="qb-door-foot__hint">${hint}</div>
      <div class="qb-door-foot__actions">
        <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
        <button type="button" class="qb-btn qb-btn--primary" data-role="add">${svgIcon(ICONS.plus)} ${escapeHtml(label)}</button>
      </div>
    </div>
  `;
}

/** Combo widget — mirrors source `comboHtml` (kos-qb-v3.js:2497–2505).
 *  Renders input + absolute dropdown. Fuzzy data source for handles/storage/
 *  lighting/appliances is the loaded `/api/door-ranges` catalogue (filtered
 *  by category — source lines 4548, 4568, 4603, 4622). For modular V1 the
 *  dropdown opens but stays "Type to search the catalogue…" until the
 *  shared catalogue cache is loaded — UX shell is exact to source. */
function comboInput(role: string, value: string, placeholder: string): string {
  return `
    <div class="qb-door-combo-wrap">
      <input type="text" class="qb-door-input" data-role="${role}" value="${escapeHtml(value)}" placeholder="${escapeHtml(placeholder)}" autocomplete="off" />
      <div class="qb-door-combo" data-combo-for="${role}" role="listbox"></div>
    </div>
  `;
}

/** Per-category product pool predicate. Mirrors source's filter blocks
 *  in `_qbv3HandSearch` / `StorSearch` / `LgtSearch` / `AppSearch`
 *  (kos-qb-v3.js:4548, 4568, 4603, 4622). */
type PoolKind = "handles" | "storage" | "lighting" | "appliances";

function poolFor(kind: PoolKind, all: ProductRecord[]): ProductRecord[] {
  if (kind === "handles") {
    return all.filter((p) => p.productCategory === "Handles");
  }
  if (kind === "storage") {
    return all.filter((p) => /storage/i.test(p.productCategory ?? ""));
  }
  if (kind === "lighting") {
    return all.filter((p) =>
      /lighting|light|led|spot|pendant/i.test((p.productCategory ?? "") + " " + (p.category ?? "")));
  }
  // appliances
  return all.filter((p) =>
    /appliance|oven|hob|fridge|freezer|dishwasher|microwave|extractor|cooker hood/i.test(
      (p.productCategory ?? "") + " " + (p.category ?? "") + " " + (p.name ?? ""),
    ));
}

interface ComboBindOpts {
  /** Pool predicate kind. Determines which catalogue subset to filter. */
  pool: PoolKind;
  onInput: (val: string) => void;
  onPick: (product: ProductRecord) => void;
}

/** Wires a combo widget's focus/blur/input to open/close the dropdown.
 *  Drives the dropdown with fuzzyFilter over the shared products cache,
 *  matching source's `_qbv3{Hand,Stor,Lgt,App}Search` handlers. */
function wireCombo(host: HTMLElement, role: string, opts: ComboBindOpts): void {
  const input = host.querySelector<HTMLInputElement>(`[data-role='${role}']`);
  const combo = host.querySelector<HTMLElement>(`[data-combo-for='${role}']`);
  if (!input || !combo) return;

  let lastHits: ProductRecord[] = [];

  const paint = (q: string): void => {
    const all = getCachedProducts();
    if (!all) {
      combo.innerHTML = `<div class="qb-door-combo__empty">Loading catalogue…</div>`;
      return;
    }
    const pool = poolFor(opts.pool, all);
    const hits = fuzzyFilter(q, pool, (p) => `${p.name} ${p.description ?? ""} ${p.sku ?? ""}`, {
      threshold: 30,
      limit: 20,
      favKey: (p) => String(p.id),
    });
    lastHits = hits.map((h) => h.item);
    if (lastHits.length === 0) {
      combo.innerHTML = `<div class="qb-door-combo__empty">No matches${q ? ` for "${escapeHtml(q)}"` : ""} — type freely or pick another supplier</div>`;
      return;
    }
    combo.innerHTML = lastHits.map((p, i) => {
      const meta = (p.supplierName ?? "") + (p.sku ? ` · ${p.sku}` : "");
      return `<div class="qb-door-combo__item" data-pick-idx="${i}" role="option">
        <span class="qb-door-combo__name">${highlight(String(p.name ?? ""), q)}</span>
        ${meta ? `<span class="qb-door-combo__meta">${escapeHtml(meta)}</span>` : ""}
      </div>`;
    }).join("");
  };

  const open = (): void => {
    combo.classList.add("is-open");
    paint(input.value);
  };
  const close = (): void => { window.setTimeout(() => combo.classList.remove("is-open"), 200); };

  input.addEventListener("focus", () => open());
  input.addEventListener("blur", () => close());
  input.addEventListener("input", () => {
    opts.onInput(input.value);
    open();
  });
  combo.addEventListener("mousedown", (e) => {
    const target = e.target as HTMLElement | null;
    const itemEl = target?.closest<HTMLElement>("[data-pick-idx]");
    if (!itemEl) return;
    e.preventDefault();
    const idx = Number(itemEl.dataset["pickIdx"]);
    const picked = lastHits[idx];
    if (picked) opts.onPick(picked);
  });

  // Kick off cache load. Re-paint when it lands.
  if (!getCachedProducts()) {
    void loadAllProducts().then(() => {
      if (combo.classList.contains("is-open")) paint(input.value);
    });
  }
}

function pickerWrap(title: string, hint: string, body: string): string {
  return `
    <div class="qb-door-picker__inner">
      <div class="qb-door-picker__head">
        <span class="qb-door-picker__title">${escapeHtml(title)}</span>
        <span class="qb-door-picker__hint">${escapeHtml(hint)}</span>
      </div>
      ${body}
    </div>
  `;
}

export function buildBaseItem(
  store: QuoteStore,
  cat: ItemCategory,
  supplier: string,
  code: string,
  desc: string,
  qty: number,
  listPrice: number,
  extras: Partial<QuoteItem> = {},
): QuoteItem {
  const item: QuoteItem = {
    id: newId("i"),
    category: cat,
    supplier,
    code,
    desc,
    qty,
    width: 0, height: 0, depth: 0,
    notes: "",
    listPrice,
    discPct: defaultDisc(supplier),
    tradePrice: 0,
    markupMode: "pct",
    markupPct: store.get().defaults.markupPct ?? 0,
    markupGbp: 0,
    retailPrice: 0,
    ...extras,
  };
  recalcItem(item, null);
  return item;
}

export function pushItemToZone(store: QuoteStore, zoneId: string, item: QuoteItem): void {
  const z = store.get().zones.find((zo) => zo.id === zoneId) ?? store.get().zones[0];
  if (!z) return;
  store.set((d) => {
    const target = d.zones.find((zo) => zo.id === z.id) ?? d.zones[0];
    if (target) target.items.push(item);
  });
}

function defaultZoneId(store: QuoteStore): string {
  return store.get().zones[0]?.id ?? "";
}

export interface SimplePickerHandle {
  unmount(): void;
}

// ── HANDLES ───────────────────────────────────────────────────────────────

interface HandleState extends BaseState { finish: string; }

export function mountHandlePicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: HandleState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.handle.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "", finish: "",
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Handle picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row full">
        <label class="qb-door-label">Search handle (fuzzy) · or type a description</label>
        ${comboInput("desc", s.desc, "e.g. brushed brass bar, T-bar 192mm…")}
      </div>
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div><label class="qb-door-label">Description</label><input class="qb-door-input" data-role="desc2" value="${escapeHtml(s.desc)}" /></div>
        <div><label class="qb-door-label">List price (£)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" /></div>
      </div>
      ${footer("Add handle")}
    `);
    wireBase(host, s, store, paint);
    wireCombo(host, "desc", {
      pool: "handles",
      onInput: (val) => { s.desc = val; },
      onPick: (p) => {
        s.desc = String(p.name ?? "");
        s.sku = String(p.sku ?? s.sku);
        if (p.supplierName && typeof p.supplierName === "string") s.supplier = p.supplierName;
        bumpFav(String(p.id ?? ""));
        paint();
      },
    });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = s.desc || "Handle";
      const code = s.sku || `${s.supplier}-HANDLE`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "handles", s.supplier, code, label, s.qty, s.listPrice));
      s.sku = ""; s.desc = ""; s.listPrice = 0; s.qty = 1;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── STORAGE ───────────────────────────────────────────────────────────────

interface StorageState extends BaseState { subType: string; }

export function mountStoragePicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: StorageState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.storage.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "",
    subType: store.get().defaults.storage.subType || "Bins",
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Storage picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row full">
        <label class="qb-door-label">Search storage solutions</label>
        ${comboInput("desc", s.desc, "e.g. magic corner, larder pull-out, bin drawer…")}
      </div>
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div><label class="qb-door-label">Description</label><input class="qb-door-input" data-role="desc2" value="${escapeHtml(s.desc)}" /></div>
        <div><label class="qb-door-label">List price (£)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" /></div>
      </div>
      ${footer("Add storage")}
    `);
    wireBase(host, s, store, paint);
    wireCombo(host, "desc", {
      pool: "storage",
      onInput: (val) => { s.desc = val; },
      onPick: (p) => {
        s.desc = String(p.name ?? "");
        s.sku = String(p.sku ?? s.sku);
        bumpFav(String(p.id ?? ""));
        paint();
      },
    });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = s.desc || s.subType || "Storage solution";
      const code = s.sku || `${s.supplier}-STORAGE`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "storage", s.supplier, code, label, s.qty, s.listPrice));
      s.sku = ""; s.desc = ""; s.listPrice = 0; s.qty = 1;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── WORKTOPS ──────────────────────────────────────────────────────────────

interface WorktopState extends BaseState {
  material: string; edge: string;
  length: number; width: number; thickness: number;
}

export function mountWorktopPicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: WorktopState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.worktop.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "",
    material: store.get().defaults.worktop.material || "Laminate",
    edge: "Square",
    length: 3000, width: 600, thickness: 38,
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Worktop picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Material</label><select class="qb-door-input" data-role="material">${typeOpts(WORKTOP_MATERIALS, s.material)}</select></div>
        <div><label class="qb-door-label">Edge profile</label><select class="qb-door-input" data-role="edge">${typeOpts(WORKTOP_EDGES, s.edge)}</select></div>
      </div>
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Length (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="length" value="${s.length}" /></div>
        <div><label class="qb-door-label">Width / depth (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="width" value="${s.width}" /></div>
        <div><label class="qb-door-label">Thickness (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="thickness" value="${s.thickness}" /></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row two">
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div><label class="qb-door-label">List price (£)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" /></div>
      </div>
      ${footer("Add worktop")}
    `);
    wireBase(host, s, store, paint);
    host.querySelector<HTMLSelectElement>("[data-role='material']")?.addEventListener("change", (e) => { s.material = (e.target as HTMLSelectElement).value; });
    host.querySelector<HTMLSelectElement>("[data-role='edge']")?.addEventListener("change", (e) => { s.edge = (e.target as HTMLSelectElement).value; });
    bindNum(host, "length", (n) => { s.length = n; });
    bindNum(host, "width", (n) => { s.width = n; });
    bindNum(host, "thickness", (n) => { s.thickness = n; });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = `${s.material} ${s.length} × ${s.width} × ${s.thickness}mm`;
      const desc = `${label}${s.edge ? ` (edge: ${s.edge})` : ""}`;
      const code = s.sku || `${s.supplier}-WT-${s.material.slice(0, 3).toUpperCase()}-${s.length}`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "worktops", s.supplier, code, desc, s.qty, s.listPrice, {
        material: s.material, length: s.length, width: s.width, thickness: s.thickness,
      }));
      s.sku = ""; s.listPrice = 0;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── PANELS ────────────────────────────────────────────────────────────────

interface DimState extends BaseState {
  type: string; width: number; height: number; depth: number;
}

export function mountPanelPicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: DimState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.panel.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "",
    type: store.get().defaults.panel.type || "Base End Panel",
    width: 720, height: 720, depth: 18,
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Panel picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Type</label><select class="qb-door-input" data-role="type">${typeOpts(PANEL_TYPES, s.type)}</select></div>
      </div>
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Width / Length (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="width" value="${s.width}" /></div>
        <div><label class="qb-door-label">Height (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="height" value="${s.height}" /></div>
        <div><label class="qb-door-label">Depth / thickness (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="depth" value="${s.depth}" /></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row two">
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div><label class="qb-door-label">List price (£)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" /></div>
      </div>
      ${footer("Add panel")}
    `);
    wireBase(host, s, store, paint);
    host.querySelector<HTMLSelectElement>("[data-role='type']")?.addEventListener("change", (e) => { s.type = (e.target as HTMLSelectElement).value; });
    bindNum(host, "width", (n) => { s.width = n; });
    bindNum(host, "height", (n) => { s.height = n; });
    bindNum(host, "depth", (n) => { s.depth = n; });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = `${s.type} ${s.width} × ${s.height} × ${s.depth}mm`;
      const code = s.sku || `${s.supplier}-PNL-${s.type.slice(0, 3).toUpperCase()}`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "panels", s.supplier, code, label, s.qty, s.listPrice, {
        width: s.width, height: s.height, depth: s.depth,
      }));
      s.sku = ""; s.listPrice = 0;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── PLINTH (uses dims like Panel; default type 'Plinth') ─────────────────

export function mountPlinthPicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: DimState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.plinth.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "",
    type: store.get().defaults.plinth.type || "Plinth",
    width: 3000, height: 150, depth: 18,
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Plinth / Cornice / Pelmet picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Type</label><select class="qb-door-input" data-role="type">${typeOpts(PLINTH_TYPES, s.type)}</select></div>
      </div>
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Length (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="width" value="${s.width}" /></div>
        <div><label class="qb-door-label">Height (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="height" value="${s.height}" /></div>
        <div><label class="qb-door-label">Thickness (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="depth" value="${s.depth}" /></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row two">
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div><label class="qb-door-label">List price (£)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" /></div>
      </div>
      ${footer("Add")}
    `);
    wireBase(host, s, store, paint);
    host.querySelector<HTMLSelectElement>("[data-role='type']")?.addEventListener("change", (e) => { s.type = (e.target as HTMLSelectElement).value; });
    bindNum(host, "width", (n) => { s.width = n; });
    bindNum(host, "height", (n) => { s.height = n; });
    bindNum(host, "depth", (n) => { s.depth = n; });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = `${s.type} ${s.width}mm`;
      const code = s.sku || `${s.supplier}-PLT-${s.type.slice(0, 3).toUpperCase()}`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "plinth", s.supplier, code, label, s.qty, s.listPrice, {
        width: s.width, height: s.height, depth: s.depth,
      }));
      s.sku = ""; s.listPrice = 0;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── LIGHTING ──────────────────────────────────────────────────────────────

interface LightingState extends BaseState {
  type: string; length: number; watts: number;
}

export function mountLightingPicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: LightingState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.lighting.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "",
    type: store.get().defaults.lighting.type || "LED Strip",
    length: 0, watts: 0,
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Lighting picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Type</label><select class="qb-door-input" data-role="type">${typeOpts(LIGHTING_TYPES, s.type)}</select></div>
      </div>
      <div class="qb-cab-row full">
        <label class="qb-door-label">Search lighting catalogue</label>
        ${comboInput("desc", s.desc, "e.g. 5m warm white LED strip 24V, plinth spot kit…")}
      </div>
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div><label class="qb-door-label">Length (mm)</label><input type="number" min="0" step="1" class="qb-door-input" data-role="length" value="${s.length}" /></div>
        <div><label class="qb-door-label">Power (W)</label><input type="number" min="0" step="0.1" class="qb-door-input" data-role="watts" value="${s.watts}" /></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row two">
        <div><label class="qb-door-label">Description</label><input class="qb-door-input" data-role="desc2" value="${escapeHtml(s.desc)}" /></div>
        <div><label class="qb-door-label">List price (£)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" /></div>
      </div>
      ${footer("Add lighting")}
    `);
    wireBase(host, s, store, paint);
    wireCombo(host, "desc", {
      pool: "lighting",
      onInput: (val) => { s.desc = val; },
      onPick: (p) => {
        s.desc = String(p.name ?? "");
        s.sku = String(p.sku ?? s.sku);
        if (p.supplierName && typeof p.supplierName === "string") s.supplier = p.supplierName;
        bumpFav(String(p.id ?? ""));
        paint();
      },
    });
    host.querySelector<HTMLSelectElement>("[data-role='type']")?.addEventListener("change", (e) => { s.type = (e.target as HTMLSelectElement).value; });
    bindNum(host, "length", (n) => { s.length = n; });
    bindNum(host, "watts", (n) => { s.watts = n; });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = `${s.type}${s.desc ? ` — ${s.desc}` : ""}`;
      const code = s.sku || `${s.supplier}-LGT-${s.type.slice(0, 3).toUpperCase()}`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "lighting", s.supplier, code, label, s.qty, s.listPrice, {
        length: s.length,
      }));
      s.sku = ""; s.desc = ""; s.listPrice = 0;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── APPLIANCES ────────────────────────────────────────────────────────────

interface ApplianceState extends BaseState {
  type: string; brand: string; model: string;
}

export function mountAppliancePicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: ApplianceState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.appliance.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "",
    type: store.get().defaults.appliance.type || "Oven",
    brand: "", model: "",
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Appliance picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier / distributor</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Category</label><select class="qb-door-input" data-role="type">${typeOpts(APPLIANCE_TYPES, s.type)}</select></div>
      </div>
      <div class="qb-cab-row full">
        <label class="qb-door-label">Search appliance catalogue</label>
        ${comboInput("desc", s.desc, "e.g. Bosch HBG7341B1B Series 8 single oven, Neff T56FT60X0…")}
      </div>
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Brand</label><input class="qb-door-input" data-role="brand" value="${escapeHtml(s.brand)}" placeholder="e.g. Bosch" /></div>
        <div><label class="qb-door-label">Model</label><input class="qb-door-input" data-role="model" value="${escapeHtml(s.model)}" placeholder="e.g. HBG7341B1B" /></div>
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row two">
        <div><label class="qb-door-label">Description</label><input class="qb-door-input" data-role="desc2" value="${escapeHtml(s.desc)}" /></div>
        <div><label class="qb-door-label">List price (£)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" /></div>
      </div>
      ${footer("Add appliance")}
    `);
    wireBase(host, s, store, paint);
    wireCombo(host, "desc", {
      pool: "appliances",
      onInput: (val) => { s.desc = val; },
      onPick: (p) => {
        s.desc = String(p.name ?? "");
        s.sku = String(p.sku ?? s.sku);
        if (p.supplierName && typeof p.supplierName === "string") s.supplier = p.supplierName;
        bumpFav(String(p.id ?? ""));
        paint();
      },
    });
    host.querySelector<HTMLSelectElement>("[data-role='type']")?.addEventListener("change", (e) => { s.type = (e.target as HTMLSelectElement).value; });
    host.querySelector<HTMLInputElement>("[data-role='brand']")?.addEventListener("input", (e) => { s.brand = (e.target as HTMLInputElement).value; });
    host.querySelector<HTMLInputElement>("[data-role='model']")?.addEventListener("input", (e) => { s.model = (e.target as HTMLInputElement).value; });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = `${s.brand ? s.brand + " " : ""}${s.model || s.type || "Appliance"}`;
      const desc = `${label}${s.desc ? ` — ${s.desc}` : ""}`;
      const code = s.sku || `${s.supplier}-APP-${s.type.slice(0, 3).toUpperCase()}`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "appliances", s.supplier, code, desc, s.qty, s.listPrice));
      s.sku = ""; s.desc = ""; s.brand = ""; s.model = ""; s.listPrice = 0;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── SUNDRIES ──────────────────────────────────────────────────────────────

interface SundryState extends BaseState { type: string; }

export function mountSundriesPicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: SundryState = {
    zoneId: defaultZoneId(store),
    supplier: store.get().defaults.sundries.supplier || "TKC",
    qty: 1, listPrice: 0, sku: "", desc: "",
    type: store.get().defaults.sundries.type || "Brackets",
  };
  function paint(): void {
    host.innerHTML = pickerWrap("Sundries picker", `${s.supplier} catalogue`, `
      <div class="qb-cab-row three">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Supplier</label><select class="qb-door-input" data-role="supplier">${supplierOpts(s.supplier)}</select></div>
        <div><label class="qb-door-label">Type</label><select class="qb-door-input" data-role="type">${typeOpts(SUNDRY_TYPES, s.type)}</select></div>
      </div>
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Code / SKU</label><input class="qb-door-input" data-role="sku" value="${escapeHtml(s.sku)}" /></div>
        <div style="grid-column:span 2"><label class="qb-door-label">Description</label><input class="qb-door-input" data-role="desc" value="${escapeHtml(s.desc)}" placeholder="e.g. Soudal Fix-All white 290ml cartridge" /></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      <div class="qb-cab-row full">
        <label class="qb-door-label">List price (£ each)</label>
        <input type="number" min="0" step="0.01" class="qb-door-input" data-role="list-price" value="${s.listPrice}" />
      </div>
      ${footer("Add sundry")}
    `);
    wireBase(host, s, store, paint);
    host.querySelector<HTMLSelectElement>("[data-role='type']")?.addEventListener("change", (e) => { s.type = (e.target as HTMLSelectElement).value; });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = s.desc || s.type || "Sundry";
      const code = s.sku || `${s.supplier}-SUN-${s.type.slice(0, 3).toUpperCase()}`;
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "sundries", s.supplier, code, label, s.qty, s.listPrice));
      s.sku = ""; s.desc = ""; s.listPrice = 0;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── LABOUR ────────────────────────────────────────────────────────────────

interface LabourState {
  zoneId: string; type: string; desc: string;
  hours: number; rate: number; qty: number;
}

export function mountLabourPicker(host: HTMLElement, store: QuoteStore): SimplePickerHandle {
  const s: LabourState = {
    zoneId: defaultZoneId(store),
    type: "Installation",
    desc: "",
    hours: 1,
    rate: store.get().defaults.labour.rate || 0,
    qty: 1,
  };
  function paint(): void {
    const total = (Number(s.hours) || 0) * (Number(s.rate) || 0);
    host.innerHTML = pickerWrap("Labour picker", "Self-inputted · not from a supplier catalogue", `
      <div class="qb-cab-row two">
        <div><label class="qb-door-label">Add to zone</label><select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select></div>
        <div><label class="qb-door-label">Labour type</label><select class="qb-door-input" data-role="type">${typeOpts(LABOUR_TYPES, s.type)}</select></div>
      </div>
      <div class="qb-cab-row full">
        <label class="qb-door-label">Description</label>
        <input class="qb-door-input" data-role="desc" value="${escapeHtml(s.desc)}" placeholder="e.g. 2-day kitchen installation, 2 fitters" />
      </div>
      <div class="qb-cab-row four">
        <div><label class="qb-door-label">Hours / days / units</label><input type="number" min="0" step="0.5" class="qb-door-input" data-role="hours" value="${s.hours}" /></div>
        <div><label class="qb-door-label">Rate (£ / unit)</label><input type="number" min="0" step="0.01" class="qb-door-input" data-role="rate" value="${s.rate}" /></div>
        <div><label class="qb-door-label">Total cost (£)</label><input type="number" readonly class="qb-door-input qb-cab-total-readonly" value="${total.toFixed(2)}" /></div>
        <div><label class="qb-door-label">Quantity</label><input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" /></div>
      </div>
      ${footer("Add labour", "Self-inputted · not from a supplier catalogue")}
    `);
    host.querySelector<HTMLSelectElement>("[data-role='zone']")?.addEventListener("change", (e) => { s.zoneId = (e.target as HTMLSelectElement).value; });
    host.querySelector<HTMLSelectElement>("[data-role='type']")?.addEventListener("change", (e) => { s.type = (e.target as HTMLSelectElement).value; });
    host.querySelector<HTMLInputElement>("[data-role='desc']")?.addEventListener("input", (e) => { s.desc = (e.target as HTMLInputElement).value; });
    host.querySelector<HTMLInputElement>("[data-role='hours']")?.addEventListener("input", (e) => {
      s.hours = Number((e.target as HTMLInputElement).value) || 0;
      paint();
    });
    host.querySelector<HTMLInputElement>("[data-role='rate']")?.addEventListener("input", (e) => {
      s.rate = Number((e.target as HTMLInputElement).value) || 0;
      paint();
    });
    host.querySelector<HTMLInputElement>("[data-role='qty']")?.addEventListener("input", (e) => { s.qty = Math.max(1, Number((e.target as HTMLInputElement).value) || 1); });
    host.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", () => {
      s.desc = ""; s.hours = 1; s.qty = 1;
      paint();
    });
    host.querySelector<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      const label = `${s.type}${s.desc ? ` — ${s.desc}` : ""}`;
      const lineTotal = (Number(s.hours) || 0) * (Number(s.rate) || 0);
      const code = `LABOUR-${s.type.slice(0, 3).toUpperCase()}`;
      const desc = `${label} · ${s.hours} × £${s.rate.toFixed(2)}`;
      // Labour bypasses the supplier-discount path — line stores it under
      // 'Labour' (source line 4507) with discPct=0.
      pushItemToZone(store, s.zoneId, buildBaseItem(store, "labour", "Labour", code, desc, s.qty, lineTotal));
      s.desc = ""; s.hours = 1; s.qty = 1;
      paint();
    });
  }
  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ── Shared wiring helpers ─────────────────────────────────────────────────

function wireBase<S extends BaseState>(
  host: HTMLElement,
  s: S,
  store: QuoteStore,
  paint: () => void,
): void {
  host.querySelector<HTMLSelectElement>("[data-role='zone']")?.addEventListener("change", (e) => { s.zoneId = (e.target as HTMLSelectElement).value; });
  host.querySelector<HTMLSelectElement>("[data-role='supplier']")?.addEventListener("change", (e) => {
    s.supplier = (e.target as HTMLSelectElement).value;
    paint();
  });
  host.querySelector<HTMLInputElement>("[data-role='qty']")?.addEventListener("input", (e) => { s.qty = Math.max(1, Number((e.target as HTMLInputElement).value) || 1); });
  host.querySelector<HTMLInputElement>("[data-role='sku']")?.addEventListener("input", (e) => { s.sku = (e.target as HTMLInputElement).value; });
  // Both desc inputs (search + desc field) write to the same state slot.
  host.querySelector<HTMLInputElement>("[data-role='desc']")?.addEventListener("input", (e) => { s.desc = (e.target as HTMLInputElement).value; });
  host.querySelector<HTMLInputElement>("[data-role='desc2']")?.addEventListener("input", (e) => { s.desc = (e.target as HTMLInputElement).value; });
  host.querySelector<HTMLInputElement>("[data-role='list-price']")?.addEventListener("input", (e) => { s.listPrice = Number((e.target as HTMLInputElement).value) || 0; });
  host.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", () => {
    // Soft reset — keep zone + supplier, clear typed fields.
    s.sku = ""; s.desc = ""; s.listPrice = 0; s.qty = 1;
    paint();
  });
  void store; // store is needed by callers for the bindings — referenced indirectly.
}

function bindNum(host: HTMLElement, role: string, set: (n: number) => void): void {
  host.querySelector<HTMLInputElement>(`[data-role='${role}']`)?.addEventListener("input", (e) => {
    set(Number((e.target as HTMLInputElement).value) || 0);
  });
}
